import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  WindowBounds,
  WindowContain,
  WindowEdge,
  WindowInteraction,
  WindowPoint,
  WindowRect,
  WindowSize,
} from "./Window.types";
import { WINDOW_HANDLE_HEIGHT, WINDOW_MARGIN } from "./constants";

type GeometryOptions = {
  open: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  defaultPosition?: WindowPoint;
  defaultSize: WindowSize;
  minSize: WindowSize;
  maxSize?: WindowSize;
  contain: WindowContain;
};

type SizeLimits = {
  minSize: WindowSize;
  maxSize?: WindowSize;
};

function limit(value: number, min: number, max: number | undefined): number {
  return Math.max(Math.min(value, max ?? Infinity), min);
}

type Geometry = {
  rect: WindowRect | null;
  interaction: WindowInteraction;
  maximized: boolean;
  startMove: (event: React.PointerEvent) => void;
  startResize: (event: React.PointerEvent, edge: WindowEdge) => void;
  toggleMaximize: () => void;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function viewportSize(): WindowSize {
  const root = document.documentElement;

  return { width: root.clientWidth, height: root.clientHeight };
}

function viewportBounds(): WindowBounds {
  const { width, height } = viewportSize();

  return {
    left: WINDOW_MARGIN,
    top: WINDOW_MARGIN,
    right: width - WINDOW_MARGIN,
    bottom: height - WINDOW_MARGIN,
  };
}

// The box the window has to stay inside, or null when it may hang out
function resolveBounds(contain: WindowContain): WindowBounds | null {
  if (!contain) {
    return null;
  }

  if (contain === true) {
    return viewportBounds();
  }

  const container = contain.current;

  if (!container) {
    return null;
  }

  const { left, top, right, bottom } = container.getBoundingClientRect();

  return { left, top, right, bottom };
}

function fillRect(bounds: WindowBounds): WindowRect {
  return {
    x: bounds.left,
    y: bounds.top,
    width: bounds.right - bounds.left,
    height: bounds.bottom - bounds.top,
  };
}

function resizeRect(
  origin: WindowRect,
  edge: WindowEdge,
  dx: number,
  dy: number,
  bounds: WindowBounds | null,
  { minSize, maxSize }: SizeLimits,
): WindowRect {
  const right = origin.x + origin.width;
  const bottom = origin.y + origin.height;

  const rect = { ...origin };

  if (edge.includes("e")) {
    rect.width = clamp(
      origin.width + dx,
      minSize.width,
      Math.min(
        maxSize?.width ?? Infinity,
        (bounds?.right ?? Infinity) - rect.x,
      ),
    );
  } else if (edge.includes("w")) {
    rect.width = clamp(
      origin.width - dx,
      minSize.width,
      Math.min(maxSize?.width ?? Infinity, right - (bounds?.left ?? -Infinity)),
    );
    rect.x = right - rect.width;
  }

  if (edge.includes("s")) {
    rect.height = clamp(
      origin.height + dy,
      minSize.height,
      Math.min(
        maxSize?.height ?? Infinity,
        (bounds?.bottom ?? Infinity) - rect.y,
      ),
    );
  } else if (edge.includes("n")) {
    rect.height = clamp(
      origin.height - dy,
      minSize.height,
      Math.min(
        maxSize?.height ?? Infinity,
        bottom - (bounds?.top ?? -Infinity),
      ),
    );
    rect.y = bottom - rect.height;
  }

  return rect;
}

function clampRect(
  rect: WindowRect,
  bounds: WindowBounds | null,
  { minSize, maxSize }: SizeLimits,
): WindowRect {
  // Uncontained, only the header has to stay grabbable
  if (!bounds) {
    const viewport = viewportSize();

    return {
      width: limit(rect.width, minSize.width, maxSize?.width),
      height: limit(rect.height, minSize.height, maxSize?.height),
      x: clamp(
        rect.x,
        WINDOW_MARGIN - rect.width + WINDOW_HANDLE_HEIGHT,
        viewport.width - WINDOW_HANDLE_HEIGHT,
      ),
      y: clamp(rect.y, WINDOW_MARGIN, viewport.height - WINDOW_HANDLE_HEIGHT),
    };
  }

  const width = limit(
    Math.min(rect.width, bounds.right - bounds.left),
    minSize.width,
    maxSize?.width,
  );

  const height = limit(
    Math.min(rect.height, bounds.bottom - bounds.top),
    minSize.height,
    maxSize?.height,
  );

  return {
    width,
    height,
    x: clamp(rect.x, bounds.left, bounds.right - width),
    y: clamp(rect.y, bounds.top, bounds.bottom - height),
  };
}

export function useWindowGeometry({
  open,
  anchorRef,
  defaultPosition,
  defaultSize,
  minSize,
  maxSize,
  contain,
}: GeometryOptions): Geometry {
  const [rect, setRect] = useState<WindowRect | null>(null);
  const [interaction, setInteraction] = useState<WindowInteraction>(null);
  const [restore, setRestore] = useState<WindowRect | null>(null);

  const maximized = restore !== null;

  const limits = useMemo(() => ({ minSize, maxSize }), [minSize, maxSize]);

  useEffect(() => {
    if (!open || rect) {
      return;
    }

    const anchor = anchorRef?.current?.getBoundingClientRect();

    const placed = defaultPosition ?? {
      x: anchor ? anchor.right - defaultSize.width : viewportSize().width,
      y: anchor ? anchor.bottom + WINDOW_MARGIN : WINDOW_MARGIN,
    };

    setRect(
      clampRect(
        { ...defaultSize, ...placed },
        resolveBounds(contain) ?? viewportBounds(),
        limits,
      ),
    );
  }, [open, rect, anchorRef, defaultPosition, defaultSize, limits, contain]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function reclamp() {
      const bounds = resolveBounds(contain);

      if (maximized) {
        const box = bounds ?? viewportBounds();

        setRect(clampRect(fillRect(box), box, limits));

        return;
      }

      setRect((current) =>
        current ? clampRect(current, bounds, limits) : current,
      );
    }

    // Also applies a contain that changed while the window was open
    reclamp();

    window.addEventListener("resize", reclamp);
    window.addEventListener("scroll", reclamp, { passive: true });

    return () => {
      window.removeEventListener("resize", reclamp);
      window.removeEventListener("scroll", reclamp);
    };
  }, [open, contain, limits, maximized]);

  const drag = useCallback(
    (
      event: React.PointerEvent,
      mode: Exclude<WindowInteraction, null>,
      edge: WindowEdge = "se",
    ) => {
      const origin = rect;

      if (event.button !== 0 || !origin || maximized) {
        return;
      }

      event.preventDefault();

      const startX = event.clientX;
      const startY = event.clientY;
      const bounds = resolveBounds(contain);

      // Text selection would otherwise follow the pointer across the page
      const previousSelect = document.documentElement.style.userSelect;

      document.documentElement.style.userSelect = "none";

      const handleMove = (moveEvent: PointerEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;

        if (mode === "move") {
          setRect(
            clampRect(
              { ...origin, x: origin.x + dx, y: origin.y + dy },
              bounds,
              limits,
            ),
          );

          return;
        }

        setRect(resizeRect(origin, edge, dx, dy, bounds, limits));
      };

      const handleUp = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleUp);
        document.documentElement.style.userSelect = previousSelect;
        setInteraction(null);
      };

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", handleUp);
      setInteraction(mode);
    },
    [rect, limits, contain, maximized],
  );

  const startMove = useCallback(
    (event: React.PointerEvent) => drag(event, "move"),
    [drag],
  );

  const startResize = useCallback(
    (event: React.PointerEvent, edge: WindowEdge) =>
      drag(event, "resize", edge),
    [drag],
  );

  const toggleMaximize = useCallback(() => {
    if (restore) {
      setRect(clampRect(restore, resolveBounds(contain), limits));
      setRestore(null);

      return;
    }

    if (!rect) {
      return;
    }

    const box = resolveBounds(contain) ?? viewportBounds();

    setRestore(rect);
    setRect(clampRect(fillRect(box), box, limits));
  }, [restore, rect, contain, limits]);

  return {
    rect,
    interaction,
    maximized,
    startMove,
    startResize,
    toggleMaximize,
  };
}
